import { useMemo } from 'react';
import { PayLaterDataProps, NULL_VALUE_FALLBACK } from '../sharedTypes';
import { useContainerWidth } from '@lendica/hooks';
import {
    DealDisplayStatus,
    DealStatus,
    currencyFormatter,
    safeDateFormatter,
} from '@lendica/utils';
import { Box } from '@mui/material';
import { StatsCard, StatsCardContent, PieChart, FundingActivityChart } from '@lendica/components';
import { DaylitTheme } from '@lendica/theme';

export const CreditStatsRow = ({
    data,
    containerBreakpoints,
}: {
    data: PayLaterDataProps;
    containerBreakpoints: ReturnType<typeof useContainerWidth>;
}) => {
    const availableCredit = data.approval?.funds_available || 0;
    const totalCredit = data.approval?.qualified_amount || 0;
    const totalOutstanding =
        data.deals
            ?.filter(deal => deal.status !== DealDisplayStatus.Rejected)
            .reduce((acc, deal) => acc + deal.total_remaining, 0) || 0;
    const numberOfDeals = data.approval?.number_of_active_deals || 0;
    const totalDealsFunded =
        data.deals?.filter(deal => deal.deal_status !== DealStatus.Submitted).length || 0;

    const fundingActivityData = useMemo(() => {
        // Create date range for the last 5 years
        const endDate = new Date();
        const startDate = new Date();
        startDate.setFullYear(endDate.getFullYear() - 5);

        // Generate all dates in the range
        const allDates = new Map<string, { amount: number; count: number }>();
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            const dateKey = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD format
            allDates.set(dateKey, { amount: 0, count: 0 });
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // If we have deals, aggregate them into the date range
        if (data.deals) {
            data.deals
                .filter(deal => deal.deal_status !== DealStatus.Submitted && deal.funded_date)
                .forEach(deal => {
                    const dealDate = new Date(deal.funded_date);

                    // Only include deals within the last 5 years
                    if (dealDate >= startDate && dealDate <= endDate) {
                        const dateKey = dealDate.toISOString().split('T')[0];

                        if (allDates.has(dateKey)) {
                            const group = allDates.get(dateKey)!;
                            group.amount += deal.gross_funded_amount;
                            group.count += 1;
                        }
                    }
                });
        }

        // Convert to array format and sort
        return Array.from(allDates.entries())
            .map(([date, data]) => ({
                date,
                amount: data.amount,
                count: data.count,
            }))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }, [data.deals]);

    const firstFundedDate = data.deals?.reduce((acc, deal) => {
        const dealDate = new Date(deal.funded_date || '');
        if (dealDate < acc) {
            return dealDate;
        }
        return acc;
    }, new Date());

    const getUsedPercent = (totalCredit: number, available: number) => {
        if (totalCredit === 0) {
            return 100;
        }
        return Math.ceil(((totalCredit - available) * 100) / totalCredit);
    };

    const getAvailablePercent = (totalCredit: number, available: number) => {
        if (totalCredit === 0) {
            return 0;
        }
        return Math.floor((available * 100) / totalCredit);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: containerBreakpoints.isWide ? 'row' : 'column',
                    flexWrap: 'wrap',
                    gap: 2,
                    width: '100%',
                }}
            >
                <StatsCard
                    sx={{
                        flex: '0 0 auto',
                        minWidth: 'fit-content',
                    }}
                >
                    <Box sx={{ width: 90, height: 90, flexShrink: 0 }}>
                        <PieChart
                            data={[
                                {
                                    value: getAvailablePercent(totalCredit, availableCredit),
                                    label: 'Available',
                                    color: DaylitTheme.Palette.Sunrise,
                                    id: 'centered',
                                },
                                {
                                    value: getUsedPercent(totalCredit, availableCredit),
                                    label: 'Used',
                                    color: DaylitTheme.Palette.WarmStone,
                                },
                            ]}
                            margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                        />
                    </Box>
                    <StatsCardContent
                        title="Available Credit"
                        value={currencyFormatter(availableCredit, true) || NULL_VALUE_FALLBACK}
                        description={`of ${
                            currencyFormatter(totalCredit, true) || NULL_VALUE_FALLBACK
                        } credit line`}
                        align="left"
                        sx={{
                            flex: 'unset',
                        }}
                    />
                </StatsCard>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        flex: 1,
                    }}
                >
                    <StatsCard sx={{ flex: 1 }}>
                        <StatsCardContent
                            title="Unpaid Balance"
                            value={currencyFormatter(totalOutstanding, true) || NULL_VALUE_FALLBACK}
                            description={`of ${numberOfDeals} bills in total`}
                        />
                    </StatsCard>
                    <StatsCard sx={{ flex: 1 }}>
                        <StatsCardContent
                            title="Total Bills Funded"
                            value={totalDealsFunded}
                            description={`since ${safeDateFormatter(
                                firstFundedDate?.toISOString() || '',
                                'MMM d, yyyy'
                            )}`}
                            align="right"
                        />
                    </StatsCard>
                </Box>
                <StatsCard
                    sx={{
                        flex: 1,
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        position: 'relative',
                        minWidth: 350,
                        pl: 0,
                        pr: 1.5,
                        paddingY: 1,
                    }}
                >
                    <FundingActivityChart data={fundingActivityData} />
                </StatsCard>
            </Box>
        </Box>
    );
};
